import React, { useEffect, useRef, useState } from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  TextInput,
  View,
  TouchableOpacity,
} from 'react-native';

import {
  PinCodeDispatchMethod,
  Platform as ReCaptchaPlatform,
} from 'apollo/queries/autogenerate/schemas-public';
import useInitiateAuth from 'shared/modules/auth/hooks/use-initiate-auth';
import useValidatePin from 'shared/modules/auth/hooks/use-validate-pin';
import CodeInput from 'shared/modules/common/components/atoms/CodeInput';
import Icon from 'shared/modules/common/components/atoms/Icon';
import url from 'shared/modules/common/constants/url';

type SessionCredentials = {
  email: string;
  phone: string;
  sessionToken: string;
};

type Props = {
  /**
   * Invoked when component is mounted. The code field is auto-focused at this point.
   */
  onFocus?: () => void;
  /**
   * Invoked when an error has occurred during voice call invocation or OTP submission
   */
  onError?: (err: Error) => void;
  /**
   * Invoked when one-time password is valid
   */
  onSuccess?: () => void;
  /**
   * The valid email, phone, and sessionToken combination returned by Signin
   */
  sessionCredentials: SessionCredentials;
  /**
   * WEB | IOS | ANDROID
   */
  reCaptchaPlatform: ReCaptchaPlatform;
  /**
   *  Every signin attempt requires a new ReCaptcha token. This function should return a Promise with the ReCaptcha token as the resolved value.
   */
  getRecaptchaTokenFunction: () => Promise<string>;
};

const PinCode = (props: Props) => {
  const initiateAuth = useInitiateAuth();
  const validatePin = useValidatePin();

  /**
   * Session token changes for each failed OTP attempt
   */
  const [sessionToken, setSessionToken] = useState(
    props.sessionCredentials.sessionToken
  );

  const [showError, setShowError] = useState(false);

  const formRef = useRef<View>(null);
  const codeInputRef = useRef<TextInput>(null);

  useEffect(() => {
    // Automatically focus the input
    codeInputRef.current?.focus();
    props.onFocus?.();

    const htmlForm = (formRef.current as unknown) as HTMLFormElement;

    /**
     * If this is rendered on the Web, OTP autofill tells the form to submit.
     * But a form submission causes the browser to reload, so we don't want that default behavior.
     * Instead, this should be passed to the props.onSuccess callback.
     *
     * Q: Why don't we just remove the <form />? Just use <div />.
     * A: Because accessibility. And many features do not work correctly if we are not semantic. So please be semantic!
     */
    const dontSubmit = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
    };

    htmlForm?.addEventListener('submit', dontSubmit);

    return () => {
      htmlForm?.removeEventListener('submit', dontSubmit);
    };
  }, []);

  const onVoiceCallInvoke = async () => {
    const reCaptchaToken = await props.getRecaptchaTokenFunction();

    try {
      const newSessionToken = await initiateAuth({
        email: props.sessionCredentials.email,
        phone: props.sessionCredentials.phone,
        method: PinCodeDispatchMethod.VOICE,
        reCaptchaPlatform: props.reCaptchaPlatform,
        reCaptchaToken,
      });

      setSessionToken(newSessionToken);
    } catch (err) {
      props.onError?.(err as Error);
    }
  };

  const onOTPFulfill = async (code: string) => {
    setShowError(false);

    try {
      await validatePin({ code, sessionToken });

      props.onSuccess?.();
    } catch (err) {
      setShowError(true);

      /**
       * Data returned by catch is untypeable,
       * so all we can do is use TypeScript's 'as' to assert the data type
       *
       * If the result has a sessionToken key then it must be a new OTP attempt
       */
      if ((err as any).sessionToken) {
        setSessionToken((err as any).sessionToken as string);
      } else {
        props.onError?.(err as Error);
      }
    }
  };

  return (
    <View>
      <View
        style={style.root}
        {...Platform.select({
          web: { accessibilityRole: 'form', ref: formRef },
        })}
      >
        <View>
          <Text style={[style.text, style.title]}>MyPaidyにログイン</Text>
          <Text style={[style.text, { fontSize: 14 }]}>
            携帯電話に届いた認証コードを入力して下さい。
          </Text>
          <View style={style.spacer} />
          <CodeInput
            onFulFill={onOTPFulfill}
            showError={showError}
            errorMessage="認証コードに誤りがあります"
            style={style.codeField}
            accessibilityLabel="Enter 4 digit PIN code"
            id="pin-code-field"
            ref={codeInputRef}
          />
        </View>
      </View>
      <OTPVoiceCallContainer onVoiceCallInvoke={onVoiceCallInvoke} />
    </View>
  );
};

type EmailReminderPinCodeProps = {
  onFulFill: (code: string) => void;
  onVoiceCallInvoke: () => void;
  showError: boolean;
};

export const EmailReminderPinCode = (props: EmailReminderPinCodeProps) => {
  const formRef = useRef<View>(null);
  const codeInputRef = useRef<TextInput>(null);

  useEffect(() => {
    // Automatically focus the input
    codeInputRef.current?.focus();

    const htmlForm = (formRef.current as unknown) as HTMLFormElement;

    const dontSubmit = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
    };

    htmlForm?.addEventListener('submit', dontSubmit);

    return () => {
      htmlForm?.removeEventListener('submit', dontSubmit);
    };
  }, []);

  return (
    <View>
      <View
        style={style.root}
        {...Platform.select({
          web: { accessibilityRole: 'form', ref: formRef },
        })}
      >
        <View>
          <Text style={[style.text, style.title]}>認証コード入力</Text>
          <Text style={[style.text, { fontSize: 14 }]}>
            SMSに送信された4桁の認証コードを入力してください。
          </Text>
          <View style={style.spacer} />
          <CodeInput
            onFulFill={props.onFulFill}
            style={style.codeField}
            errorMessage={'認証コードに誤りがあります'}
            showError={props.showError}
            id="edit-otp-code-field"
            ref={codeInputRef}
          />
          <OTPVoiceCallContainer onVoiceCallInvoke={props.onVoiceCallInvoke} />
        </View>
      </View>
    </View>
  );
};

type OTPVoiceCallContainerProps = {
  onVoiceCallInvoke: () => void;
};

const COUNTDOWN_LENGTH = 30;

const OTPVoiceCallContainer = ({
  onVoiceCallInvoke,
}: OTPVoiceCallContainerProps) => {
  const [countdownTick, setCountdownTick] = useState(COUNTDOWN_LENGTH);
  const [isVoiceCallInvoked, setIsVoiceCallInvoked] = useState(false);

  useEffect(() => {
    let timer: number;

    if (countdownTick) {
      // https://github.com/Microsoft/TypeScript/issues/30128#issuecomment-807394387
      timer = +global.setTimeout(() => {
        setCountdownTick(countdownTick - 1);
      }, 1000);
    }

    return () => clearTimeout(timer);
  });

  const onClickUseVoiceCall = () => {
    setCountdownTick(COUNTDOWN_LENGTH);
    setIsVoiceCallInvoked(true);
    onVoiceCallInvoke();
  };

  return (
    <View style={style.faqContainer}>
      <View style={style.faqTitle}>
        <Icon name="help-sms" size="24" style={style.icon} />
        <Text style={style.faqTitleText}>
          {!isVoiceCallInvoked && countdownTick > 0
            ? `${countdownTick}秒以内にSMSが届かない場合`
            : 'SMSが届いていませんか？'}
        </Text>
      </View>
      <Text style={style.itemText}>下記のいずれかをお試しください。</Text>
      <View style={style.faqItem}>
        <Icon name="bullet-black" size="24" style={style.icon} />
        <TouchableOpacity
          disabled={
            (!isVoiceCallInvoked && countdownTick > 0) ||
            (!!isVoiceCallInvoked && countdownTick > 0)
          }
          onPress={onClickUseVoiceCall}
          style={style.voiceCall}
        >
          <Text
            style={[
              style.itemText,
              !isVoiceCallInvoked && countdownTick > 0
                ? style.disabledLink
                : !!isVoiceCallInvoked && countdownTick > 0
                ? style.disabledLinkGray
                : style.primaryLink,
            ]}
          >
            {!isVoiceCallInvoked && countdownTick > 0
              ? '自動音声案内で認証コードを受け取ることができるようになります'
              : '自動音声案内で認証コードを受け取る'}
          </Text>
        </TouchableOpacity>
      </View>
      <View style={style.faqItem}>
        <Icon name="bullet-black" size="24" style={style.icon} />
        <Text style={style.itemText}>
          {`よくあるご質問の「`}
          <TouchableOpacity accessibilityRole="link" href={url.smsFaq}>
            <Text style={[style.itemText, style.primaryLink]}>
              ペイディからSMSが届きません
            </Text>
          </TouchableOpacity>
          {`」を確認してもう一度試す`}
        </Text>
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'space-between',
  },
  text: {
    marginBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  codeField: {
    width: 272,
    alignSelf: 'center',
  },
  spacer: {
    marginBottom: 48,
  },

  disabledLink: {
    color: '#1c1c1c',
  },
  disabledLinkGray: {
    color: '#e1e1e1',
  },
  primaryLink: {
    color: '#E52581',
    textDecorationLine: 'underline',
  },
  faqContainer: {
    marginTop: 58,
    marginLeft: 8,
    marginRight: 7,
  },
  faqTitle: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  icon: {
    marginRight: 8,
  },
  faqTitleText: {
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 24,
  },
  itemText: {
    fontWeight: '300',
    fontSize: 12,
    lineHeight: 24,
  },
  faqItem: {
    flex: 1,
    flexDirection: 'row',
    marginTop: 8,
  },
  voiceCall: {
    flex: 1,
  },
});

export default PinCode;
